import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { switchOrganization } from '../redux/actions/organizationActions';

const selectOrganizationImpersonationState = createSelector(
  [(state) => state.user, (state) => state.organizations],
  (user, organizations) => ({
    impersonatedOrganization: user?.impersonatedOrganization,
    defaultOrganization: user?.defaultOrganization,
    uid: user?.uid,
    currentOrganization: organizations.currentOrganization,
  })
);

export default function useOrganizationImpersonation() {
  const { impersonatedOrganization, defaultOrganization, uid, currentOrganization } = useSelector(
    selectOrganizationImpersonationState
  );
  const dispatch = useDispatch();

  const isImpersonatingOrganization = useMemo(() => {
    if (!impersonatedOrganization) return false;
    return impersonatedOrganization !== defaultOrganization;
  }, [impersonatedOrganization, defaultOrganization]);

  const isOwnOrganization = useCallback(
    (id) => {
      if (!id) return false;
      return defaultOrganization === id;
    },
    [defaultOrganization]
  );

  // If organization is not supplied, clears the impersonation. Returns the
  // thunk's promise -- without it, callers awaiting a failed write see success.
  const switchToOrganization = useCallback(
    /**
     * @param {import('@wonderschool/common-base-types').OrganizationType | null} [organization]
     * @returns {Promise<unknown>}
     */
    async (organization) => {
      if (!uid) return;

      return dispatch(switchOrganization(organization ?? null, uid));
    },
    [dispatch, uid]
  );

  return {
    isImpersonatingOrganization,
    impersonatedOrganizationId: impersonatedOrganization,
    defaultOrganizationId: defaultOrganization,
    activeOrganization: currentOrganization,
    isOwnOrganization,
    switchToOrganization,
  };
}
